// Source unique des clés VITE_* injectables au runtime via window.ENV.
// Le `satisfies` fait échouer tsc si une clé n'existe pas dans ImportMetaEnv (vite-env.d.ts).
import { collectEnvValidationErrors } from '../types/schemas/env.schema';

export const RUNTIME_ENV_KEYS = [
  'VITE_APP_ENV',
  'VITE_DEBUG_MODE',
  'VITE_LAYOUT_ERROR',
  'VITE_DSN_MULTI_SORT_ENABLED',
  'VITE_BACK_EDI_URL',
  'VITE_CLOUDID_DOMAIN',
  'VITE_CLOUDID_ACCOUNT_URL',
  'VITE_CLOUDID_CLIENT_ID',
  'VITE_CLOUDID_AUDIENCE',
  'VITE_LOGGER_INSIGHT_CONNECTION_STRING',
  'VITE_NEWRELIC_LICENSE_KEY',
  'VITE_NEWRELIC_ACCOUNT_ID',
  'VITE_NEWRELIC_TRUST_KEY',
  'VITE_NEWRELIC_APP_ID',
  'VITE_KEY_TOKEN_EXPIRATION',
  'VITE_DATE_RANGE_DEFAULT_LENGTH',
  'VITE_DATE_RANGE_MAX_LENGTH',
  // Ajoutez ici les autres variables nécessaires
] as const satisfies readonly (keyof ImportMetaEnv)[];

// Type declaration pour la configuration runtime
declare global {
  interface Window {
    ENV?: Partial<Record<(typeof RUNTIME_ENV_KEYS)[number], string>>;
  }
}

/**
 * Récupère une variable d'environnement depuis window.ENV (runtime) ou import.meta.env (build-time)
 * Cela permet de supporter à la fois le développement local, la production Docker
 * et les déploiements avec configuration injectée au runtime (par exemple via /config.js).
 *
 * Priorité : window.ENV (runtime) > import.meta.env (build-time)
 * Les valeurs vides dans window.ENV sont ignorées pour conserver la valeur build-time.
 */
export function getEnvVar(key: string): string | boolean | undefined {
  // En production (Docker ou Azure Web App), utilise window.ENV
  if (window.ENV) {
    const runtimeValue = window.ENV[key as keyof typeof window.ENV];
    if (runtimeValue !== undefined && runtimeValue !== '') {
      return runtimeValue;
    }
  }

  // En développement ou fallback, utilise import.meta.env
  return import.meta.env[key];
}

export function getEnvVarString(key: string, defaultValue = ''): string {
  const value = getEnvVar(key);
  return typeof value === 'string' ? value : defaultValue;
}

/**
 * Parse une variable d'environnement booléenne de manière robuste.
 * Accepte '1'/'0', 'true'/'false' (chaînes) et boolean natif.
 */
export function getEnvVarBool(key: string, defaultValue = false): boolean {
  const value = getEnvVar(key);
  if (value === undefined) return defaultValue;
  if (typeof value === 'boolean') return value;
  return value === '1' || value === 'true';
}

// Exporter les variables d'environnement avec fallback
export const ENV = {
  APP_ENV: getEnvVar('VITE_APP_ENV') || 'none',
  DEBUG_MODE: getEnvVarBool('VITE_DEBUG_MODE', false),
  LAYOUT_ERROR: getEnvVarBool('VITE_LAYOUT_ERROR', true),
  DSN_MULTI_SORT_ENABLED: getEnvVarBool('VITE_DSN_MULTI_SORT_ENABLED', false),
  BACK_EDI_URL: getEnvVar('VITE_BACK_EDI_URL') || '',
  CLOUDID_DOMAIN: getEnvVarString('VITE_CLOUDID_DOMAIN') || '',
  CLOUDID_ACCOUNT_URL: getEnvVarString('VITE_CLOUDID_ACCOUNT_URL') || '',
  CLOUDID_CLIENT_ID: getEnvVarString('VITE_CLOUDID_CLIENT_ID') || '',
  CLOUDID_AUDIENCE: getEnvVarString('VITE_CLOUDID_AUDIENCE') || '',
  LOGGER_INSIGHT_CONNECTION_STRING: getEnvVarString('VITE_LOGGER_INSIGHT_CONNECTION_STRING'),
  NEWRELIC_LICENSE_KEY: getEnvVarString('VITE_NEWRELIC_LICENSE_KEY'),
  NEWRELIC_ACCOUNT_ID: getEnvVarString('VITE_NEWRELIC_ACCOUNT_ID'),
  NEWRELIC_TRUST_KEY: getEnvVarString('VITE_NEWRELIC_TRUST_KEY'),
  NEWRELIC_APP_ID: getEnvVarString('VITE_NEWRELIC_APP_ID'),
  KEY_TOKEN_EXPIRATION: getEnvVarString('VITE_KEY_TOKEN_EXPIRATION'),
  DATE_RANGE_DEFAULT_LENGTH: getEnvVarString('VITE_DATE_RANGE_DEFAULT_LENGTH'),
  DATE_RANGE_MAX_LENGTH: getEnvVarString('VITE_DATE_RANGE_MAX_LENGTH'),
  // Ajoutez ici les autres variables nécessaires
};

// Validation non bloquante de la configuration au chargement du module : les clés en échec sont
// exposées (sans jamais logger les valeurs) pour permettre au bootstrap de l'application de remonter
// l'incident via le logger configuré.
export const ENV_VALIDATION_ERRORS: string[] = collectEnvValidationErrors(ENV);
